import { useEffect, useMemo, useState, FormEvent } from 'react';
import {
  Plus,
  Upload,
  Search,
  Filter,
  Receipt,
  Pencil,
  Trash2,
  Tag,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import api from '../services/api';
import { PageLoading } from '../components/common/Loading';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/common/Toast';

interface Transaction {
  id: number;
  date: string;
  payee: string;
  amount: number;
  type: 'expense' | 'income';
  envelope_id?: number;
  envelope_name?: string;
  notes?: string;
  created_at: string;
}

interface Envelope {
  id: number;
  name: string;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type ImportDuplicateRow = {
  rowNumber: number;
  accountNumber: string;
  postDate: string;
  description: string;
  debit: number | null;
  credit: number | null;
  balance: number | null;
  reason: 'existing' | 'in_file';
};

type ImportInvalidRow = {
  rowNumber: number;
  reason: string;
};

type TransactionImportResult = {
  processed: number;
  imported: number;
  duplicates: number;
  invalid: number;
  duplicateRows: ImportDuplicateRow[];
  invalidRows: ImportInvalidRow[];
};

type ImportedAccountTransaction = {
  id: number;
  account_number: string;
  post_date: string;
  check_number: string | null;
  description: string;
  debit: number | null;
  credit: number | null;
  status: string | null;
  balance: number | null;
  is_duplicate: number;
};

type ImportedFilters = {
  account_number: string;
  duplicates: 'all' | 'only' | 'exclude';
  start_date: string;
  end_date: string;
  search: string;
};

type Filters = {
  envelope_id: string;
  type: '' | 'expense' | 'income';
  start_date: string;
  end_date: string;
  search: string;
};

export default function Transactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Transaction | null>(null);

  const [viewMode, setViewMode] = useState<'budget' | 'imported'>('budget');
  const [importedTransactions, setImportedTransactions] = useState<ImportedAccountTransaction[]>([]);
  const [importedMeta, setImportedMeta] = useState<PaginationMeta | null>(null);
  const [confirmDeleteImported, setConfirmDeleteImported] = useState<ImportedAccountTransaction | null>(null);
  const [selectedImportedTransaction, setSelectedImportedTransaction] = useState<ImportedAccountTransaction | null>(null);

  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [selectedBudgetTransaction, setSelectedBudgetTransaction] = useState<Transaction | null>(null);

  const [filters, setFilters] = useState<Filters>({
    envelope_id: '',
    type: '',
    start_date: '',
    end_date: '',
    search: '',
  });

  const [importedFilters, setImportedFilters] = useState<ImportedFilters>({
    account_number: '',
    duplicates: 'all',
    start_date: '',
    end_date: '',
    search: '',
  });

  const [budgetSearchDraft, setBudgetSearchDraft] = useState('');
  const [importedSearchDraft, setImportedSearchDraft] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    void loadEnvelopes();
  }, []);

  useEffect(() => {
    if (viewMode === 'imported') {
      void loadImportedTransactions();
      return;
    }

    void loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    page,
    filters.envelope_id,
    filters.type,
    filters.start_date,
    filters.end_date,
    filters.search,
    importedFilters.account_number,
    importedFilters.duplicates,
    importedFilters.start_date,
    importedFilters.end_date,
    importedFilters.search,
  ]);

  const appliedBudgetFilterCount = useMemo(() => {
    return [filters.envelope_id, filters.type, filters.start_date, filters.end_date, filters.search].filter(Boolean).length;
  }, [filters]);

  const appliedImportedFilterCount = useMemo(() => {
    return [
      importedFilters.account_number,
      importedFilters.start_date,
      importedFilters.end_date,
      importedFilters.search,
      importedFilters.duplicates !== 'all' ? 'duplicates' : '',
    ].filter(Boolean).length;
  }, [importedFilters]);

  const appliedFilterCount = viewMode === 'imported' ? appliedImportedFilterCount : appliedBudgetFilterCount;

  const loadTransactions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, meta: apiMeta } = await api.getPaginated<Transaction>('/transactions', page, 20, {
        params: {
          envelope_id: filters.envelope_id || undefined,
          type: filters.type || undefined,
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          search: filters.search || undefined,
        },
      });

      setTransactions(data);

      if (!apiMeta) {
        setMeta(null);
        return;
      }

      setMeta({
        page: apiMeta.page ?? page,
        limit: apiMeta.limit ?? 20,
        total: apiMeta.total ?? 0,
        totalPages: apiMeta.totalPages ?? 1,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const loadEnvelopes = async () => {
    try {
      const response = await api.get<{ envelopes: Envelope[] }>('/envelopes');
      setEnvelopes(response.envelopes || []);
    } catch {
      // ignore
    }
  };

  const loadImportedTransactions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, meta: apiMeta } = await api.getPaginated<ImportedAccountTransaction>('/transactions/imported', page, 20, {
        params: {
          account_number: importedFilters.account_number || undefined,
          start_date: importedFilters.start_date || undefined,
          end_date: importedFilters.end_date || undefined,
          search: importedFilters.search || undefined,
          duplicates: importedFilters.duplicates,
        },
      });

      setImportedTransactions(data);

      if (!apiMeta) {
        setImportedMeta(null);
        return;
      }

      setImportedMeta({
        page: apiMeta.page ?? page,
        limit: apiMeta.limit ?? 20,
        total: apiMeta.total ?? 0,
        totalPages: apiMeta.totalPages ?? 1,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load imported transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirmed = async (tx: Transaction) => {
    try {
      await api.delete(`/transactions/${tx.id}`);
      await loadTransactions();
    } catch (err: any) {
      setError(err.message || 'Failed to delete transaction');
    } finally {
      setConfirmDelete(null);
      setSelectedBudgetTransaction(null);
    }
  };

  const handleDeleteImportedConfirmed = async (tx: ImportedAccountTransaction) => {
    try {
      await api.delete(`/accounts/transactions/${tx.id}`);
      await loadImportedTransactions();
    } catch (err: any) {
      setError(err.message || 'Failed to delete imported transaction');
    } finally {
      setConfirmDeleteImported(null);
      setSelectedImportedTransaction(null);
    }
  };

  const toggleImportedDuplicateFlag = async (tx: ImportedAccountTransaction) => {
    try {
      const next = tx.is_duplicate === 0;
      const updated = { ...tx, is_duplicate: next ? 1 : 0 };
      await api.patch(`/accounts/transactions/${tx.id}`, { is_duplicate: next });
      setImportedTransactions((prev) => prev.map((row) => (row.id === tx.id ? updated : row)));
      setSelectedImportedTransaction((prev) => (prev && prev.id === tx.id ? updated : prev));
    } catch (err: any) {
      setError(err.message || 'Failed to update duplicate flag');
    }
  };

  const startEnvelopeAssignmentFromImported = (tx: ImportedAccountTransaction) => {
    setEditingTransaction({
      id: 0,
      date: tx.post_date,
      payee: tx.description,
      amount: tx.debit !== null ? -Math.abs(tx.debit) : tx.credit !== null ? Math.abs(tx.credit) : 0,
      type: tx.debit !== null ? 'expense' : 'income',
      created_at: new Date().toISOString(),
    });
    setShowForm(true);
  };

  const openImportedOverlay = (tx: ImportedAccountTransaction) => {
    setSelectedImportedTransaction(tx);
  };

  const openBudgetOverlay = (tx: Transaction) => {
    setSelectedBudgetTransaction(tx);
  };

  const openCreate = () => {
    setEditingTransaction(null);
    setShowForm(true);
  };

  const openEdit = (tx: Transaction) => {
    setEditingTransaction(tx);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTransaction(null);
  };

  const applyBudgetSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setFilters((prev) => ({ ...prev, search: budgetSearchDraft.trim() }));
  };

  const applyImportedSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setImportedFilters((prev) => ({ ...prev, search: importedSearchDraft.trim() }));
  };

  const clearBudgetFilters = () => {
    setBudgetSearchDraft('');
    setPage(1);
    setFilters({ envelope_id: '', type: '', start_date: '', end_date: '', search: '' });
  };

  const clearImportedFilters = () => {
    setImportedSearchDraft('');
    setPage(1);
    setImportedFilters({ account_number: '', duplicates: 'all', start_date: '', end_date: '', search: '' });
  };

  const activeListIsEmpty = viewMode === 'imported' ? importedTransactions.length === 0 : transactions.length === 0;
  if (isLoading && activeListIsEmpty) return <PageLoading />;

  const activeMeta = viewMode === 'imported' ? importedMeta : meta;

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Transactions"
        subtitle="Track income and expenses with clean filtering"
        actions={
          <>
            <button onClick={() => setShowImport(true)} className="btn btn-secondary">
              <Upload className="w-4 h-4" />
              Import CSV
            </button>
            {viewMode === 'budget' && (
              <button onClick={openCreate} className="btn btn-primary shadow-glow">
                <Plus className="w-4 h-4" />
                Add transaction
              </button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setViewMode('budget');
            setPage(1);
            setBudgetSearchDraft(filters.search);
          }}
          className={viewMode === 'budget' ? 'btn btn-primary shadow-glow' : 'btn btn-secondary'}
        >
          Budget
        </button>
        <button
          type="button"
          onClick={() => {
            setViewMode('imported');
            setPage(1);
            setImportedSearchDraft(importedFilters.search);
          }}
          className={viewMode === 'imported' ? 'btn btn-primary shadow-glow' : 'btn btn-secondary'}
        >
          Imported
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <Card
        title="Filters"
        subtitle={appliedFilterCount > 0 ? `${appliedFilterCount} active` : 'Refine results'}
        actions={
          <>
            {appliedFilterCount > 0 && (
              <button
                onClick={viewMode === 'imported' ? clearImportedFilters : clearBudgetFilters}
                className="btn btn-secondary"
              >
                Clear
              </button>
            )}
          </>
        }
      >
        <form
          onSubmit={viewMode === 'imported' ? applyImportedSearch : applyBudgetSearch}
          className="grid grid-cols-1 lg:grid-cols-12 gap-4"
        >
          <div className="lg:col-span-5">
            <label className="block text-sm font-medium text-text-muted mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <input
                type="text"
                value={viewMode === 'imported' ? importedSearchDraft : budgetSearchDraft}
                onChange={(e) =>
                  viewMode === 'imported' ? setImportedSearchDraft(e.target.value) : setBudgetSearchDraft(e.target.value)
                }
                className="input-field pl-9"
                placeholder={viewMode === 'imported' ? 'Description…' : 'Payee, notes…'}
              />
            </div>
          </div>

          {viewMode === 'budget' ? (
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-text-muted mb-2">Envelope</label>
              <select
                value={filters.envelope_id}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, envelope_id: e.target.value }));
                }}
                className="input-field"
              >
                <option value="">All envelopes</option>
                {envelopes.map((env) => (
                  <option key={env.id} value={env.id}>
                    {env.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-text-muted mb-2">Account Number</label>
              <input
                type="text"
                value={importedFilters.account_number}
                onChange={(e) => {
                  setPage(1);
                  setImportedFilters((prev) => ({ ...prev, account_number: e.target.value }));
                }}
                className="input-field"
                placeholder="Optional"
              />
            </div>
          )}

          {viewMode === 'budget' ? (
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-text-muted mb-2">Type</label>
              <select
                value={filters.type}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, type: e.target.value as Filters['type'] }));
                }}
                className="input-field"
              >
                <option value="">All</option>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
          ) : (
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-text-muted mb-2">Duplicates</label>
              <select
                value={importedFilters.duplicates}
                onChange={(e) => {
                  setPage(1);
                  setImportedFilters((prev) => ({ ...prev, duplicates: e.target.value as ImportedFilters['duplicates'] }));
                }}
                className="input-field"
              >
                <option value="all">All</option>
                <option value="only">Only marked</option>
                <option value="exclude">Exclude marked</option>
              </select>
            </div>
          )}

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-text-muted mb-2">Date</label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => {
                  setPage(1);
                  viewMode === 'imported'
                    ? setImportedFilters((prev) => ({ ...prev, start_date: e.target.value }))
                    : setFilters((prev) => ({ ...prev, start_date: e.target.value }));
                }}
                className="input-field"
              />
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => {
                  setPage(1);
                  viewMode === 'imported'
                    ? setImportedFilters((prev) => ({ ...prev, end_date: e.target.value }))
                    : setFilters((prev) => ({ ...prev, end_date: e.target.value }));
                }}
                className="input-field"
              />
            </div>
          </div>

          <div className="lg:col-span-12 flex items-center justify-end gap-3">
            <button type="submit" className="btn btn-secondary">
              <Filter className="w-4 h-4" />
              Apply
            </button>
          </div>
        </form>
      </Card>

      <Card
        title={viewMode === 'imported' ? 'Imported Transactions' : 'Transactions'}
        subtitle={
          viewMode === 'imported'
            ? importedMeta
              ? `${importedMeta.total} total`
              : undefined
            : meta
              ? `${meta.total} total`
              : undefined
        }
        actions={
          <Badge variant="default">
            {isLoading
              ? 'Loading…'
              : viewMode === 'imported'
                ? `${importedTransactions.length} shown`
                : `${transactions.length} shown`}
          </Badge>
        }
      >
        {(viewMode === 'imported' ? importedTransactions.length === 0 : transactions.length === 0) ? (
          <div className="pt-2 text-center">
            <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
              <Receipt className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text-header">No transactions</h3>
            <p className="mt-2 text-sm text-text-muted">
              {appliedFilterCount > 0
                ? 'No transactions match your filters.'
                : viewMode === 'imported'
                  ? 'Import a CSV to see imported transactions here.'
                  : 'Start tracking by adding your first transaction.'}
            </p>
            {appliedFilterCount === 0 && viewMode === 'budget' && (
              <button onClick={openCreate} className="btn btn-primary shadow-glow mt-6 inline-flex">
                <Plus className="w-4 h-4" />
                Add transaction
              </button>
            )}
          </div>
        ) : (
          viewMode === 'imported' ? (
            <div className="-mx-6 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-border bg-background-card/50">
                    <th className="table-header">Account</th>
                    <th className="table-header">Post Date</th>
                    <th className="table-header">Description</th>
                    <th className="table-header text-right hidden md:table-cell">Debit</th>
                    <th className="table-header text-right hidden md:table-cell">Credit</th>
                    <th className="table-header hidden md:table-cell">Status</th>
                    <th className="table-header text-right hidden md:table-cell">Balance</th>
                    <th className="table-header hidden md:table-cell">Duplicate</th>
                    <th className="table-header text-right hidden md:table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {importedTransactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="table-row cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => openImportedOverlay(tx)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openImportedOverlay(tx);
                        }
                      }}
                    >
                      <td className="table-cell text-text-header">{tx.account_number}</td>
                      <td className="table-cell text-text-muted">{formatDate(tx.post_date)}</td>
                      <td className="table-cell text-text-header">
                        <div className="max-w-[16rem] truncate md:max-w-none">{tx.description}</div>
                      </td>
                      <td className="table-cell text-right text-text-header hidden md:table-cell">
                        {tx.debit === null ? '—' : formatCurrency(tx.debit)}
                      </td>
                      <td className="table-cell text-right text-text-header hidden md:table-cell">
                        {tx.credit === null ? '—' : formatCurrency(tx.credit)}
                      </td>
                      <td className="table-cell text-text-muted hidden md:table-cell">{tx.status ?? '—'}</td>
                      <td className="table-cell text-right text-text-header hidden md:table-cell">
                        {tx.balance === null ? '—' : formatCurrency(tx.balance)}
                      </td>
                      <td className="table-cell hidden md:table-cell">
                        {tx.is_duplicate ? <Badge variant="warning">Marked</Badge> : <span className="text-xs text-text-muted">—</span>}
                      </td>
                      <td className="table-cell text-right hidden md:table-cell">
                        <div className="inline-flex items-center gap-2">
                           <button
                             type="button"
                             className="btn btn-primary px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEnvelopeAssignmentFromImported(tx);
                            }}
                            title="Assign to envelope"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                           <button
                             type="button"
                             className="btn btn-secondary px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleImportedDuplicateFlag(tx);
                            }}
                            title={tx.is_duplicate ? 'Unmark duplicate' : 'Mark duplicate'}
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                           <button
                             type="button"
                             className="btn btn-danger px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteImported(tx);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="-mx-6 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-border bg-background-card/50">
                    <th className="table-header">Date</th>
                    <th className="table-header">Payee</th>
                    <th className="table-header">Envelope</th>
                    <th className="table-header">Type</th>
                    <th className="table-header text-right hidden md:table-cell">Amount</th>
                    <th className="table-header text-right hidden md:table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {transactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="table-row cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => openBudgetOverlay(tx)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openBudgetOverlay(tx);
                        }
                      }}
                    >
                      <td className="table-cell text-text-muted">{formatDate(tx.date)}</td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-text-header">{tx.payee}</div>
                        {tx.notes && <div className="hidden md:block text-xs text-text-muted truncate max-w-xs mt-1">{tx.notes}</div>}
                      </td>
                      <td className="table-cell">
                        {tx.envelope_name ? <Badge>{tx.envelope_name}</Badge> : <span className="text-xs text-text-muted">—</span>}
                      </td>
                      <td className="table-cell">
                        {tx.type === 'income' ? (
                          <Badge variant="success">Income</Badge>
                        ) : (
                          <Badge variant="warning">Expense</Badge>
                        )}
                      </td>
                      <td className="table-cell text-right hidden md:table-cell">
                        <span className={`text-sm font-semibold ${tx.type === 'income' ? 'text-success' : 'text-text-header'}`}>
                          {tx.type === 'income' ? '+' : '-'}
                          {formatCurrency(Math.abs(tx.amount))}
                        </span>
                      </td>
                      <td className="table-cell text-right hidden md:table-cell">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(tx);
                            }}
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete(tx);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {activeMeta && activeMeta.totalPages > 1 && (
          <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-sm text-text-muted">
                Showing {(activeMeta.page - 1) * activeMeta.limit + 1} to {Math.min(activeMeta.page * activeMeta.limit, activeMeta.total)} of {activeMeta.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4" />
                Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= activeMeta.totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={Boolean(selectedImportedTransaction)}
        onClose={() => setSelectedImportedTransaction(null)}
        title="Imported transaction"
        description={
          selectedImportedTransaction
            ? `${selectedImportedTransaction.account_number} • ${formatDate(selectedImportedTransaction.post_date)}`
            : undefined
        }
        size="lg"
        footer={
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={() => setSelectedImportedTransaction(null)} className="btn btn-secondary">
              Close
            </button>
            <div className="flex items-center justify-end gap-2">
              {selectedImportedTransaction && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary px-3 py-2"
                    onClick={() => {
                      startEnvelopeAssignmentFromImported(selectedImportedTransaction);
                      setSelectedImportedTransaction(null);
                    }}
                    title="Assign to envelope"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary px-3 py-2"
                    onClick={() => void toggleImportedDuplicateFlag(selectedImportedTransaction)}
                    title={selectedImportedTransaction.is_duplicate ? 'Unmark duplicate' : 'Mark duplicate'}
                  >
                    <Tag className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-3 py-2"
                    onClick={() => setConfirmDeleteImported(selectedImportedTransaction)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        }
      >
        {selectedImportedTransaction && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailField label="Account" value={selectedImportedTransaction.account_number} />
              <DetailField label="Post Date" value={formatDate(selectedImportedTransaction.post_date)} />
              <DetailField label="Debit" value={selectedImportedTransaction.debit === null ? '' : formatCurrency(selectedImportedTransaction.debit)} />
              <DetailField label="Credit" value={selectedImportedTransaction.credit === null ? '' : formatCurrency(selectedImportedTransaction.credit)} />
              <DetailField label="Status" value={selectedImportedTransaction.status ?? ''} />
              <DetailField label="Balance" value={selectedImportedTransaction.balance === null ? '' : formatCurrency(selectedImportedTransaction.balance)} />
              <DetailField label="Duplicate Tag" value={selectedImportedTransaction.is_duplicate ? 'Marked' : ''} />
            </div>
            <DetailField label="Description" value={selectedImportedTransaction.description} />
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(selectedBudgetTransaction)}
        onClose={() => setSelectedBudgetTransaction(null)}
        title={selectedBudgetTransaction ? selectedBudgetTransaction.payee : 'Transaction'}
        description={selectedBudgetTransaction ? formatDate(selectedBudgetTransaction.date) : undefined}
        size="lg"
        footer={
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={() => setSelectedBudgetTransaction(null)} className="btn btn-secondary">
              Close
            </button>
            <div className="flex items-center justify-end gap-2">
              {selectedBudgetTransaction && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary px-3 py-2"
                    onClick={() => {
                      openEdit(selectedBudgetTransaction);
                      setSelectedBudgetTransaction(null);
                    }}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-3 py-2"
                    onClick={() => setConfirmDelete(selectedBudgetTransaction)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        }
      >
        {selectedBudgetTransaction && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailField label="Date" value={formatDate(selectedBudgetTransaction.date)} />
              <DetailField label="Type" value={selectedBudgetTransaction.type} />
              <DetailField label="Envelope" value={selectedBudgetTransaction.envelope_name ?? ''} />
              <DetailField
                label="Amount"
                value={`${selectedBudgetTransaction.type === 'income' ? '+' : '-'}${formatCurrency(Math.abs(selectedBudgetTransaction.amount))}`}
              />
            </div>
            {selectedBudgetTransaction.notes && <DetailField label="Notes" value={selectedBudgetTransaction.notes} />}
          </div>
        )}
      </Modal>

      <TransactionFormModal
        isOpen={showForm}
        onClose={closeForm}
        envelopes={envelopes}
        transaction={editingTransaction}
        onSubmit={async () => {
          await loadTransactions();
          closeForm();
        }}
      />

      <TransactionImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => {
          setViewMode('imported');
          setPage(1);
          setImportedSearchDraft(importedFilters.search);
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && void handleDeleteConfirmed(confirmDelete)}
        title="Delete transaction"
        message={confirmDelete ? `Delete transaction “${confirmDelete.payee}” from ${formatDate(confirmDelete.date)}?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={Boolean(confirmDeleteImported)}
        onClose={() => setConfirmDeleteImported(null)}
        onConfirm={() => confirmDeleteImported && void handleDeleteImportedConfirmed(confirmDeleteImported)}
        title="Delete imported transaction"
        message={
          confirmDeleteImported
            ? `Delete imported transaction “${confirmDeleteImported.description}” on ${formatDate(confirmDeleteImported.post_date)}?`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

function TransactionFormModal({
  isOpen,
  onClose,
  onSubmit,
  envelopes,
  transaction,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  envelopes: Envelope[];
  transaction: Transaction | null;
}) {
  const [formData, setFormData] = useState({
    date: transaction?.date || new Date().toISOString().split('T')[0],
    payee: transaction?.payee || '',
    amount: transaction?.amount ? Math.abs(transaction.amount).toString() : '',
    type: transaction?.type || 'expense',
    envelope_id: transaction?.envelope_id?.toString() || '',
    notes: transaction?.notes || '',
  });

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      date: transaction?.date || new Date().toISOString().split('T')[0],
      payee: transaction?.payee || '',
      amount: transaction?.amount ? Math.abs(transaction.amount).toString() : '',
      type: transaction?.type || 'expense',
      envelope_id: transaction?.envelope_id?.toString() || '',
      notes: transaction?.notes || '',
    });
    setError('');
    setIsSubmitting(false);
  }, [isOpen, transaction]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.date) {
      setError('Date is required');
      return;
    }

    if (!formData.payee.trim()) {
      setError('Payee is required');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
        const payload = {
          date: formData.date,
          payee: formData.payee.trim(),
          amount,
          type: formData.type,
          envelope_id: formData.envelope_id ? parseInt(formData.envelope_id, 10) : null,
          notes: formData.notes.trim() || null,
        };

      if (transaction && transaction.id !== 0) {
        await api.put(`/transactions/${transaction.id}`, payload);
      } else {
        await api.post('/transactions', payload);
      }

      await onSubmit();
    } catch (err: any) {
      setError(err.message || 'Failed to save transaction');
      setIsSubmitting(false);
    }
  };

  const typeButton = (value: 'expense' | 'income') => {
    const active = formData.type === value;

    const base = 'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2';

    if (value === 'expense') {
      return (
        <button
          type="button"
          onClick={() => setFormData((p) => ({ ...p, type: 'expense' }))}
          className={`${base} ${active ? 'bg-warning-bg text-warning border border-warning/20' : 'bg-background-subtle/40 text-text-muted border border-border hover:bg-background-subtle'}`}
        >
          <TrendingDown className="w-4 h-4" />
          Expense
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={() => setFormData((p) => ({ ...p, type: 'income', envelope_id: '' }))}
        className={`${base} ${active ? 'bg-success-bg text-success border border-success/20' : 'bg-background-subtle/40 text-text-muted border border-border hover:bg-background-subtle'}`}
      >
        <TrendingUp className="w-4 h-4" />
        Income
      </button>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={transaction && transaction.id !== 0 ? 'Edit transaction' : 'Add transaction'}
      description="Log income and expenses to keep totals accurate."
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="transaction-form" disabled={isSubmitting} className="btn btn-primary shadow-glow">
            {isSubmitting ? 'Saving…' : transaction && transaction.id !== 0 ? 'Update' : 'Add'}
          </button>
        </div>
      }
    >
      <form id="transaction-form" onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Type</label>
          <div className="grid grid-cols-2 gap-3">{typeButton('expense')}{typeButton('income')}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData((p) => ({ ...p, amount: e.target.value }))}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Payee</label>
          <input
            type="text"
            value={formData.payee}
            onChange={(e) => setFormData((p) => ({ ...p, payee: e.target.value }))}
            className="input-field"
            placeholder="Who did you pay or receive from?"
            autoFocus
          />
        </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Envelope</label>
            <select
              value={formData.envelope_id}
              onChange={(e) => setFormData((p) => ({ ...p, envelope_id: e.target.value }))}
              className="input-field"
            >
              <option value="">Unassigned</option>
              {envelopes.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name}
                </option>
              ))}
            </select>
          </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Notes (optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
            rows={3}
            className="input-field"
            placeholder="Any details…"
          />
        </div>
      </form>
    </Modal>
  );
}

function TransactionImportModal({
  isOpen,
  onClose,
  onImported,
}: {
  isOpen: boolean;
  onClose: () => void;
  onImported?: () => void;
}) {
  const { addToast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<TransactionImportResult | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedFile(null);
    setResult(null);
    setError('');
    setIsSubmitting(false);
  }, [isOpen]);

  const importCsv = async () => {
    if (!selectedFile) {
      setError('Please choose a CSV file to import.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const csv =
        typeof (selectedFile as any).text === 'function'
          ? await (selectedFile as any).text()
          : await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(String(reader.result ?? ''));
              reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
              reader.readAsText(selectedFile);
            });
      const response = await api.post<TransactionImportResult, { csv: string }>(
        '/transactions/import',
        { csv }
      );

      setResult(response);

      addToast(
        `Imported ${response.imported}. Flagged ${response.duplicates} duplicates. ${response.invalid} invalid.`,
        response.invalid > 0 ? 'warning' : 'success'
      );

      onImported?.();
      setIsSubmitting(false);
    } catch (err: any) {
      const baseMessage = err?.message || 'Failed to import CSV';

      const missing = Array.isArray(err?.details?.missing) ? (err.details.missing as string[]) : null;
      const unexpected = Array.isArray(err?.details?.unexpected) ? (err.details.unexpected as string[]) : null;

      if (missing && missing.length > 0) {
        setError(
          `${baseMessage}. Missing headers: ${missing.join(', ')}${unexpected && unexpected.length > 0 ? `; Unexpected: ${unexpected.join(', ')}` : ''}`
        );
      } else {
        setError(baseMessage);
      }

      setIsSubmitting(false);
    }
  };

  const counts = result
    ? [
        { label: 'Processed', value: result.processed },
        { label: 'Imported', value: result.imported },
        { label: 'Duplicates', value: result.duplicates },
        { label: 'Invalid', value: result.invalid },
      ]
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import CSV"
      description="Import bank-style transaction CSVs and flag potential duplicates for review."
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Close
          </button>
          <button type="button" onClick={() => void importCsv()} disabled={isSubmitting} className="btn btn-primary shadow-glow">
            {isSubmitting ? 'Importing…' : 'Import'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="transaction-import-file" className="block text-sm font-medium text-text-muted mb-2">
            CSV file
          </label>
          <input
            id="transaction-import-file"
            type="file"
            accept=".csv,text/csv"
            className="input-field"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setSelectedFile(file);
              setResult(null);
              setError('');
            }}
          />
          <p className="mt-2 text-xs text-text-muted">
            Required headers: Account Number, Post Date, Check, Description, Debit, Credit, Status, Balance
          </p>
        </div>

        {result && (
          <Card title="Import Summary" subtitle="Potential duplicates are flagged for review">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {counts.map((c) => (
                <div key={c.label} className="p-4 rounded-xl border border-border bg-background-subtle/40">
                  <div className="text-xs text-text-muted">{c.label}</div>
                  <div className="mt-1 text-lg font-semibold text-text-header">{c.value}</div>
                </div>
              ))}
            </div>

            {(result.duplicateRows.length > 0 || result.invalidRows.length > 0) && (
              <div className="mt-6 space-y-5">
                {result.duplicateRows.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-text-header">Duplicates</h4>
                    <div className="mt-2 max-h-48 overflow-auto rounded-xl border border-border">
                      <table className="min-w-full">
                        <thead className="bg-background-card/50">
                          <tr className="border-b border-border">
                            <th className="table-header">Row</th>
                            <th className="table-header">Account</th>
                            <th className="table-header">Date</th>
                            <th className="table-header">Description</th>
                    <th className="table-header text-right hidden md:table-cell">Debit</th>
                    <th className="table-header text-right hidden md:table-cell">Credit</th>
                    <th className="table-header text-right hidden md:table-cell">Balance</th>
                            <th className="table-header">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {result.duplicateRows.map((r) => (
                            <tr key={`${r.rowNumber}-${r.accountNumber}-${r.postDate}`} className="table-row">
                              <td className="table-cell text-text-muted">{r.rowNumber}</td>
                              <td className="table-cell text-text-header">{r.accountNumber}</td>
                              <td className="table-cell text-text-muted">{r.postDate}</td>
                              <td className="table-cell text-text-header">{r.description}</td>
                              <td className="table-cell text-right text-text-header">{r.debit === null ? '—' : formatCurrency(r.debit)}</td>
                              <td className="table-cell text-right text-text-header">{r.credit === null ? '—' : formatCurrency(r.credit)}</td>
                              <td className="table-cell text-right text-text-header">{r.balance === null ? '—' : formatCurrency(r.balance)}</td>
                              <td className="table-cell text-text-muted">{r.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {result.invalidRows.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-text-header">Invalid Rows</h4>
                    <div className="mt-2 max-h-48 overflow-auto rounded-xl border border-border">
                      <table className="min-w-full">
                        <thead className="bg-background-card/50">
                          <tr className="border-b border-border">
                            <th className="table-header">Row</th>
                            <th className="table-header">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {result.invalidRows.map((r) => (
                            <tr key={`${r.rowNumber}-${r.reason}`} className="table-row">
                              <td className="table-cell text-text-muted">{r.rowNumber}</td>
                              <td className="table-cell text-text-header">{r.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </Modal>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  const display = value || '\u00A0';

  return (
    <div className="rounded-xl border border-border bg-background-subtle/30 p-4">
      <div className="text-xs font-medium text-text-muted">{label}</div>
      <div className="mt-1 text-sm text-text-header break-words">{display}</div>
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
